import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { purchaseService } from './app/services/purchaseService';
import { logout } from './app/redux/slices/authSlice';
import { setPurchasedFromApi } from './app/redux/slices/ebookAccessSlice';
import Section1 from './app/sections/Section1';
import Section3 from './app/sections/Section3';
import Section4 from './app/sections/Section4';
import Section5 from './app/sections/Section5';
import Section6 from './app/sections/Section6';
import Section7 from './app/sections/Section7';
import Section8 from './app/sections/Section8';
import Section10 from './app/sections/Section10';
import SectionCurso from './app/sections/SectionCurso';
import Checkout from './app/pages/Checkout';
import ShopPage from './app/pages/ShopPage';
import ProductDetails from './app/pages/ProductDetails';
import MyDownloadsPage from './app/pages/MyDownloadsPage';
import TermoReembolso from './app/pages/TermoReembolso';
import TermosDeUso from './app/pages/TermosDeUso';
import Login from './app/pages/Login';
import ResetPassword from './app/pages/Login/ResetPassword';
import Cadastro from './app/pages/Cadastro';
import Curso, { Aula } from './app/pages/Curso';
import ShopHeader from './app/components/ShopHeader';
import Footer from './app/sections/Footer';
import WhatsappPrompt from './app/components/WhatsappPrompt';
import GlobalStyle from './GlobalStyle';
// Componente para página principal
const HomePage = ({ modalShow, setModalShow }) => (
  <main>
    <GlobalStyle modalShow={modalShow} />
    <ShopHeader currentPage="home" />
    <Section1 />
    <Section5 />
    <Section3 modalShow={modalShow} setModalShow={setModalShow} />
    <SectionCurso />
    <Section10 />
    <Section4 />
    <Section6 />
    <Section7 />
    <Section8 />
  </main>
);

const AppContent = ({ modalShow, setModalShow }) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);

  // Quando há token: sincroniza compras do localStorage para a API e hidrata o Redux.
  // Quando NÃO há token: usa estado local (localStorage) para compras anônimas.
  useEffect(() => {
    if (!token) {
      console.log('ℹ️ [App] Sem token - usando compras locais (localStorage)');
      return;
    }
    console.log('🔐 [App] Token detectado - sincronizando e carregando compras');
    let cancelled = false;

    const syncAndLoad = async () => {
      if (user?.email) {
        // 1. Reivindicar compras órfãs da API (pagas anonimamente com este e-mail)
        try {
          const claimed = await purchaseService.claimOrphanPurchases(token, user.email, user.cpf);
          if (claimed.claimed > 0) {
            console.log(`✅ [App] ${claimed.claimed} compra(s) órfã(s) vinculada(s) à conta`);
          }
        } catch (claimErr) {
          console.warn('⚠️ [App] Falha ao reivindicar órfãs (não crítico):', claimErr.message);
        }

        // 2. Sincronizar compras do localStorage para a API (se houver itens não registrados)
        try {
          const syncResult = await purchaseService.syncAnonymousPurchasesToAccount(token, user.email);
          if (syncResult.synced > 0) {
            console.log(`✅ [App] ${syncResult.synced} compra(s) do localStorage sincronizada(s) com a API`);
          }
        } catch (syncErr) {
          console.warn('⚠️ [App] Falha ao sincronizar localStorage (não crítico):', syncErr.message);
        }
      }

      // 3. Carregar lista atualizada da API
      if (cancelled) return;
      try {
        const data = await purchaseService.listMine(token);
        if (!cancelled) {
          console.log('✅ [App] Compras carregadas da API:', data.purchases);
          dispatch(setPurchasedFromApi(data.purchases || []));
        }
      } catch (err) {
        if (err.status === 401) {
          console.log('🔐 [App] Token inválido - fazendo logout');
          dispatch(logout());
        } else {
          console.error('❌ [App] Falha ao carregar compras:', err.message);
        }
      }
    };

    syncAndLoad();
    return () => {
      cancelled = true;
    };
  }, [token, user, dispatch]);

  return (
    <>
      <Routes>
        <Route path="/" element={<HomePage modalShow={modalShow} setModalShow={setModalShow} />} />
        <Route path="/loja" element={
          <main>
            <GlobalStyle modalShow={modalShow} />
            <ShopHeader currentPage="shop" />
            <ShopPage />
          </main>
        } />
        <Route path="/produto/:productId" element={
          <main>
            <GlobalStyle modalShow={modalShow} />
            <ShopHeader currentPage="shop" />
            <ProductDetails />
          </main>
        } />
        <Route path="/checkout" element={
          <main>
            <GlobalStyle modalShow={modalShow} />
            <ShopHeader currentPage="checkout" />
            <Checkout />
          </main>
        } />
        <Route path="/minhas-compras" element={
          <main>
            <GlobalStyle modalShow={modalShow} />
            <ShopHeader currentPage="downloads" />
            <MyDownloadsPage />
          </main>
        } />
        <Route path="/termo-de-reembolso" element={
          <main>
            <GlobalStyle modalShow={modalShow} />
            <ShopHeader currentPage="termo-reembolso" />
            <TermoReembolso />
          </main>
        } />
        <Route path="/termo-de-uso-e-politica-de-privacidade" element={
          <main>
            <GlobalStyle modalShow={modalShow} />
            <ShopHeader currentPage="termos-de-uso" />
            <TermosDeUso />
          </main>
        } />
        <Route path="/login" element={
          <main>
            <GlobalStyle modalShow={modalShow} />
            <ShopHeader currentPage="login" />
            <Login />
          </main>
        } />
        <Route path="/reset-senha" element={
          <main>
            <GlobalStyle modalShow={modalShow} />
            <ShopHeader currentPage="reset-senha" />
            <ResetPassword />
          </main>
        } />
        <Route path="/cadastro" element={
          <main>
            <GlobalStyle modalShow={modalShow} />
            <ShopHeader currentPage="cadastro" />
            <Cadastro />
          </main>
        } />
        <Route path="/curso-de-fingerstyle" element={
          <main>
            <GlobalStyle modalShow={modalShow} />
            <ShopHeader currentPage="curso" />
            <Curso />
          </main>
        } />
        <Route path="/curso-de-fingerstyle/aula/:lessonId" element={
          <main>
            <GlobalStyle modalShow={modalShow} />
            <ShopHeader currentPage="curso" />
            <Aula />
          </main>
        } />
      </Routes>
      <Footer />
      <WhatsappPrompt />
    </>
  );
};

function App() {
  const [modalShow, setModalShow] = useState(false);

  return (
    <Router>
      <AppContent modalShow={modalShow} setModalShow={setModalShow} />
    </Router>
  );
}

export default App;
